import * as z from 'zod';
import axios from 'axios';
import client from '../client';

import { NEWS_PER_PAGE } from '../../utils/cmsRoutesData';

// ZOD
import { Media, Meta, type MetaType } from '../strapiSchema';

const PRIMARY_LOCALE = 'fr-FR';

const isNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const logMissingNewsForLocale = (locale: string) => {
  // eslint-disable-next-line no-console
  console.warn(`[news] No news found for locale "${locale}", skipping.`);
};

const News = z.object({
  id: z.number(),
  documentId: z.string(),  
  image: Media,
  imagePosition: z.enum(['top', 'center', 'bottom']),
  title: z.string(),
  description: z.string().nullish(),
  url: z.string(),
  publicationDate: z.string(),
});

export type NewsType = z.infer<typeof News>;

const getAllNews = async (locale: string = PRIMARY_LOCALE, page: number = 1): Promise<{
  data: Array<NewsType>, meta: MetaType
}> => {
  try {
    const response = await client.get('/api/news', {
      params: {
        locale,
        populate: '*',
        sort: ['publicationDate:desc'],
        pagination: { page, pageSize: NEWS_PER_PAGE },
      },
    });

    return {
      data: await z.array(News).parseAsync(response.data?.data),
      meta: await Meta.parseAsync(response.data?.meta),
    };
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingNewsForLocale(locale);
      return {
        data: [],
        meta: { pagination: { page, pageSize: NEWS_PER_PAGE, pageCount: 0, total: 0 } },
      };
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match News structure : ${errorMessage}`);
  }
};

const getLatestNews = async (locale: string = PRIMARY_LOCALE): Promise<Array<NewsType>> => {
  try {
    const response = await client.get('/api/news', {
      params: {
        locale,
        populate: '*',
        sort: ['publicationDate:desc'],
        pagination: {
          limit: 3
        },
      },
    });

    if (!response.data?.data?.length) {
      if (locale !== PRIMARY_LOCALE) {
        logMissingNewsForLocale(locale);
      }
      return [];
    }

    return z.array(News).parseAsync(response.data.data);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingNewsForLocale(locale);
      return [];
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match News structure : ${errorMessage}`);
  }
};

export {
  getAllNews,
  getLatestNews
};